import type { BlockStyle, ResolvedStyle } from "../types/style.js";
import type { DocumentStyles, FlowOptions, Node } from "../types/template.js";
import type { Theme } from "../types/theme.js";
import type { DataSource, FieldSchema, FieldType, PageContext } from "../types/data.js";
import type { VChild, VNode, VProps, h as hyperscript } from "../vnode/h.js";
import type { FormatContext } from "../expr/format.js";
import type { SchemaScope } from "../expr/typecheck.js";
import type { ResolvedNode } from "../resolve/resolved.js";
import type { Scope } from "../expr/evaluate.js";
import type { Issue } from "../validate/issues.js";

export type BlockCategory = "content" | "layout" | "data" | "visualisation" | "custom" | "structure";
export type LicenseTier = "community" | "pro" | "enterprise";
export type SplitMode = "none" | "lines" | "rows" | "children";
export type RenderMode = "design" | "preview";

/* ------------------------------------------------------------------ */
/* Inspector (properties panel) schema                                 */
/* ------------------------------------------------------------------ */

export interface InspectorFieldBase {
  /** Property name on `props` this field edits. Supports dot paths (`series.color`). */
  prop: string;
  label: string;
  /**
   * Help text under the control. A function when the useful wording depends on
   * the block's other settings — a table's row alias is named by the designer,
   * so "use it like `row.name`" is wrong for every table that renamed it.
   */
  description?: string | ((props: Record<string, unknown>) => string);
  /** Show only when the predicate passes. */
  when?: (props: Record<string, unknown>) => boolean;
  /** Take a full row in a two-column inspector grid. */
  fullWidth?: boolean;
}

export type SelectOption = { value: string | number | boolean; label: string; icon?: string; description?: string };

export type InspectorField =
  | (InspectorFieldBase & { kind: "text"; placeholder?: string; allowTokens?: boolean })
  | (InspectorFieldBase & { kind: "textarea"; placeholder?: string; rows?: number; allowTokens?: boolean })
  | (InspectorFieldBase & { kind: "richtext" })
  | (InspectorFieldBase & { kind: "number"; min?: number; max?: number; step?: number; unit?: string })
  | (InspectorFieldBase & { kind: "toggle" })
  | (InspectorFieldBase & { kind: "select"; options?: SelectOption[]; optionsFrom?: (props: Record<string, unknown>) => SelectOption[] })
  | (InspectorFieldBase & { kind: "segmented"; options: SelectOption[] })
  | (InspectorFieldBase & { kind: "color"; allowTransparent?: boolean })
  | (InspectorFieldBase & { kind: "font" })
  | (InspectorFieldBase & { kind: "textStyle" })
  | (InspectorFieldBase & { kind: "binding"; valueType?: FieldType | FieldType[]; placeholder?: string })
  | (InspectorFieldBase & { kind: "collection"; placeholder?: string })
  | (InspectorFieldBase & { kind: "expression"; placeholder?: string })
  | (InspectorFieldBase & { kind: "image"; allowBinding?: boolean })
  | (InspectorFieldBase & { kind: "condition" })
  | (InspectorFieldBase & { kind: "columns" })
  | (InspectorFieldBase & { kind: "series" })
  | (InspectorFieldBase & { kind: "keyValues" })
  | (InspectorFieldBase & {
      kind: "list";
      itemLabel?: string;
      fields: InspectorField[];
      /**
       * One line describing a collapsed item. Without it the list falls back to
       * whatever field looks like a name, which for a table's conditional
       * formatting rules meant showing the column id — "c5" — to a person who
       * has never seen a column id and has no way to look one up.
       */
      itemSummary?: (item: Record<string, unknown>, index: number, props: Record<string, unknown>) => string;
    })
  | (InspectorFieldBase & { kind: "custom"; component: InspectorComponent });

/**
 * Framework-neutral mount adapter for custom inspector UIs. `@broadpaper/react`
 * and `@broadpaper/angular` provide helpers that produce one from a component.
 */
export interface InspectorComponent {
  mount(element: HTMLElement, api: InspectorApi): void;
  update?(api: InspectorApi): void;
  unmount?(element: HTMLElement): void;
}

export interface InspectorApi {
  value: unknown;
  props: Record<string, unknown>;
  node: Node;
  setValue(value: unknown): void;
  setProps(patch: Record<string, unknown>): void;
  dataSources: DataSource[];
  schemaScope: SchemaScope;
  theme: Theme;
}

export interface InspectorSection {
  title: string;
  fields: InspectorField[];
  collapsed?: boolean;
  when?: (props: Record<string, unknown>) => boolean;
}

/** Which built-in style groups the inspector shows for a block. */
export type StyleGroup = "textStyle" | "typography" | "alignment" | "spacing" | "border" | "background" | "size" | "flow";

/* ------------------------------------------------------------------ */
/* Bindings                                                            */
/* ------------------------------------------------------------------ */

export type BindingKind =
  /** A single expression yielding a value. */
  | "value"
  /** An expression yielding an array. */
  | "collection"
  /** A plain string that may contain `{{ }}` tokens. */
  | "template"
  /** A rich text document containing inline tokens. */
  | "richtext"
  /** A structured or string condition. */
  | "condition"
  /** A list of objects that contain nested bindings (see `items`). Not evaluated itself. */
  | "list";

export interface BindingSpec {
  prop: string;
  kind: BindingKind;
  valueType?: FieldType | FieldType[];
  /** Nested bindings inside list props (e.g. table columns). */
  items?: BindingSpec[];
  /** For collections: props on this block that are evaluated per item (e.g. table column values). */
  scoped?: BindingSpec[];
  /** Prop holding the alias name introduced into scope for per-item bindings. */
  alias?: string;
  /**
   * The expression is evaluated per item of the block's collection by the block's
   * own `resolve` hook (e.g. table column values). The resolver leaves it alone;
   * validation checks it in the item scope.
   */
  perItem?: boolean;
}

export interface BindingInfo {
  prop: string;
  kind: BindingKind;
  expr: string;
  /** Human label such as "Client › First name". */
  label: string;
  type?: FieldType | "unknown";
}

/* ------------------------------------------------------------------ */
/* Containers                                                          */
/* ------------------------------------------------------------------ */

export interface ContainerSpec {
  /** What children this node accepts: block types, or a wildcard group. */
  accepts: Array<string | "@blocks" | "@rows" | "@columns" | "@sections">;
  min?: number;
  max?: number;
  /** Repeat children once per item of the collection in `collectionProp`. */
  repeat?: { collectionProp: string; aliasProp?: string; keepItemTogetherProp?: string; emptyMessageProp?: string; limitProp?: string };
  /** Show children only when the condition in `conditionProp` holds. */
  conditional?: { conditionProp: string; elseChildrenProp?: string };
  /** Direction children are laid out in. Default "column". */
  direction?: "column" | "row";
}

/* ------------------------------------------------------------------ */
/* Contexts                                                            */
/* ------------------------------------------------------------------ */

export interface ResolveHookContext<P> {
  props: P;
  node: Node;
  scope: Scope;
  mode: RenderMode;
  theme: Theme;
  format: FormatContext;
  evaluate(expr: string): unknown;
  evaluateIn(expr: string, vars: Record<string, unknown>): unknown;
  warn(message: string): void;
}

export interface RenderContext<P = Record<string, unknown>> {
  node: ResolvedNode;
  props: P;
  style: ResolvedStyle;
  /** Inline CSS for the block root: text + box (padding, border, background). Margin/width are applied by the wrapper. */
  css: Record<string, string>;
  theme: Theme;
  styles: DocumentStyles;
  mode: RenderMode;
  h: typeof hyperscript;
  /** Rendered child nodes (containers only). */
  children: VNode[];
  page: PageContext;
  /** Available width in px. */
  width: number;
  format: FormatContext;
  registry: BlockRegistryLike;
  /** Renders an interpolated string prop; tokens become chips in design mode. */
  text(prop: string, fallback?: string): VChild[];
  /** Renders a rich text prop. */
  richText(prop: string): VChild[];
  /** Renders a plain string value that may contain tokens (already resolved in preview). */
  textValue(value: string | undefined): VChild[];
  /** Metadata about a bound prop (for badges/chips). */
  binding(prop: string): BindingInfo | undefined;
  /** Standard attributes for a sub-element the editor should be able to target. */
  partAttrs(part: string, extra?: VProps): VProps;
  /** Placeholder shown in design mode for empty content. */
  placeholder(text: string, extra?: VProps): VNode;
}

export interface ValidateContext<P> {
  props: P;
  node: Node;
  schemaScope: SchemaScope;
  dataSources: DataSource[];
  theme: Theme;
  /** Type-checks an expression and returns its issues (already prefixed with the node). */
  checkExpression(expr: string, prop: string): Issue[];
}

export interface BlockRegistryLike {
  get(type: string): BlockDefinition | undefined;
  has(type: string): boolean;
}

/* ------------------------------------------------------------------ */
/* Definition                                                          */
/* ------------------------------------------------------------------ */

export interface BlockDefinition<P extends Record<string, unknown> = Record<string, unknown>> {
  /** Unique type id. Custom blocks should be namespaced: "acme.risk-profile". */
  type: string;
  name: string;
  description?: string;
  category: BlockCategory;
  /** Lucide icon name (e.g. "heading") or an inline `<svg>` string. */
  icon?: string;
  keywords?: string[];
  /**
   * The tier **the host** sells this block in, badged in the palette and
   * reported by validation when the host has not enabled it.
   *
   * It exists so an embedder can put their own blocks behind their own plans.
   * BroadPaper's built-in blocks never set it, and must not: the two BroadPaper
   * licences unlock identical software, and nothing in this SDK reads an
   * entitlement to decide what to allow. Chart, Table and Repeater carried
   * `"pro"` for a while, which said the opposite of what we sell.
   */
  license?: LicenseTier;
  /** Do not show in the block palette (structural nodes, deprecated types). */
  hidden?: boolean;
  /** Version of the props shape. Bump and implement `migrate` when it changes. */
  version?: number;

  defaultProps: P | (() => P);
  defaultStyle?: BlockStyle;
  defaultFlow?: FlowOptions;
  /** Document text style to inherit from ("body", "h1", ...). */
  defaultTextStyle?: string;

  inspector?: InspectorSection[];
  styleGroups?: StyleGroup[];
  /** Declares props holding expressions. Derived from `inspector` when omitted. */
  bindings?: BindingSpec[];

  container?: ContainerSpec;
  /** How the paginator may split this node. Containers default to "children", leaves to "none". */
  split?: SplitMode;
  /**
   * What the measurer should inspect: "text" reads line boxes, "table" reads rows.
   * Derived from `split` when omitted.
   */
  measure?: "text" | "table" | "none";

  /**
   * Props for a block created by dropping a data field onto the page.
   *
   * The designer lets a user drag a field out of the data panel, and what should
   * appear is not the same thing for every block: a list wants a table with a
   * column per item field, each already formatted the way the schema says; a
   * scalar wants a labelled value. Only the block knows what its own props mean,
   * so the block decides — including a custom block, which is the reason this is
   * a hook rather than a rule in the editor.
   *
   * `expr` is the bare path; `formatted` is the same path with the field's own
   * formatter appended, which is what a prop rendering the value as text should
   * use. Return undefined to take the designer's default.
   */
  fromField?(field: { expr: string; formatted: string; schema: FieldSchema; label: string }): Partial<P> | undefined;

  /** Adjust resolved props after binding resolution (compute derived data, sort, etc.). */
  resolve?(ctx: ResolveHookContext<P>): P | void;
  render(ctx: RenderContext<P>): VNode | null;
  validate?(ctx: ValidateContext<P>): Issue[];
  migrate?(props: Record<string, unknown>, fromVersion: number): P;

  canvas?: {
    /** Enables inline editing of a prop directly on the canvas. */
    inlineEdit?: { prop: string; kind: "richtext" | "text" };
    /** Layer-tree label. */
    label?: (props: P) => string;
    /** Minimum height in px for the empty design-mode placeholder. */
    minHeight?: number;
  };
}

/** Identity helper that gives full type inference for custom blocks. */
export function defineBlock<P extends Record<string, unknown>>(def: BlockDefinition<P>): BlockDefinition<P> {
  if (!def.type || !/^[a-z0-9][a-z0-9._-]*$/i.test(def.type)) {
    throw new Error(`Invalid block type "${def.type}". Use letters, digits, dots, dashes and underscores.`);
  }
  return def;
}

export function resolveDefaultProps<P extends Record<string, unknown>>(def: BlockDefinition<P>): P {
  const d = def.defaultProps;
  return typeof d === "function" ? structuredClone((d as () => P)()) : structuredClone(d);
}

export type { FieldSchema };
